import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useAtomValue } from "jotai";
import { tenantIDAtom } from "../../../atoms/GlobalAtoms";
import useCloudPermissions from "../../../hooks/useCloudPermissions";
import TenantAPI from "../../api/TenantAPI";
import { updateTenantUsers } from "../../../utils/UserUtils";
import {
  callFunctionIfExists,
  getErrorMessage,
} from "../../../utils/GeneralUtils";

// Used for updating the permissions of a user at the Cloud Scope.
const useUpdateUserCloudPermissions = () => {
  const queryClient = useQueryClient();
  const { cPermissions } = useCloudPermissions();
  const tenantID = useAtomValue(tenantIDAtom);

  const updateUserCloudPermissions = useMutation({
    mutationFn: ({
      userID,
      permissionID,
      onSuccess,
      supportTargetCloudID,
    }: {
      userID: string;
      permissionID: string;
      onSuccess: () => void;
      supportTargetCloudID: string | undefined;
    }) => {
      if (!supportTargetCloudID && !cPermissions?.cloud_modify_permissions) {
        return Promise.reject(
          "User does not have permission to update permissions"
        );
      }

      return TenantAPI.updateUserTenantPermissions({
        userID,
        permissionID,
        supportTargetCloudID,
      }).then((data) => {
        const { updatedUser } = data.data.data;

        updateTenantUsers(
          queryClient,
          supportTargetCloudID || tenantID || "",
          updatedUser
        );

        callFunctionIfExists(onSuccess);
      });
    },
  });

  return {
    updateUserCloudPermissions: updateUserCloudPermissions.mutate,
    updatingPermissions: updateUserCloudPermissions.isPending,
    updatePermissionsError: updateUserCloudPermissions.error
      ? getErrorMessage(updateUserCloudPermissions.error)
      : null,
  };
};

export default useUpdateUserCloudPermissions;
